/**
 * event_waypoint.ts — "set a waypoint to this event" (client sweep G5). Reuses the waypoint table: one marker per event
 * (found by name + spot, never duplicated), then followed with the in-world indicator.
 */
import type { DbConnection } from '../module_bindings/index.js';
import { bus } from '../bus.js';
import { myWaypoints, trackWaypoint } from './waypoints.js';
import type { EventMarker } from './event_markers.js';

// The server stores trim().slice(0, 32): a cut that ended on a space was stored one shorter, never matched again here —
// the marker was not followed and every click made another waypoint. The same rule on both sides (idempotent).
const nameFor = (m: EventMarker): string => `${m.glyph} ${m.title}`.trim().slice(0, 32).trim();
const same = (w: { name: string; posX: number; posZ: number }, m: EventMarker): boolean => w.name === nameFor(m) && Math.hypot(w.posX - m.x, w.posZ - m.z) < 1;

export function waypointToEvent(conn: DbConnection | null, m: EventMarker): void {
  if (!conn) return;
  const have = myWaypoints(conn).find((w) => same(w, m));
  if (have) { trackWaypoint(have.waypointId); return; }
  conn.reducers.createWaypoint({ name: nameFor(m), posX: m.x, posZ: m.z })
    .then(() => {
      bus.emit('atlas:toast', { text: `Waypoint set to ${m.title} — follow the marker`, kind: 'success', ms: 3500 });
      // The row lands a beat after the reducer resolves: follow it as soon as it is there (a pinned other waypoint would not move).
      let tries = 0;
      const t = window.setInterval(() => {
        const w = myWaypoints(conn).find((x) => same(x, m));
        if (w) { trackWaypoint(w.waypointId); window.clearInterval(t); } else if (++tries > 20) window.clearInterval(t);
      }, 150);
    })
    .catch((err: Error) => bus.emit('atlas:toast', { text: `Waypoint refused: ${err.message}`, kind: 'error' }));
}
